import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import FilterPills from 'components/Table/FilterPills';
import TableSearch from 'components/Table/TableSearch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Col, Form, Row, Spinner } from 'react-bootstrap';

import { useShowSnackbar } from '../../../../hooks';
import { DEV_URL, rdelete, rget, rpost } from '../../../../utils/requests';
import { trimInvalidDate } from 'helpers/api/utils';
import SortingIcon from 'components/Table/SortingIcon';
import FormInput from 'components/FormInput/index';
import Select from 'components/UnifiedSelect';
import Table from 'components/UnifiedMarkupTable';

const DOMAIN_PATTERN_REGEX = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{1,62}$/i;
const TLD_PATTERN_REGEX = /^[a-z][a-z0-9-]{1,62}$/i;

const getField = (record, camelKey, pascalKey) => record?.[camelKey] ?? record?.[pascalKey];

const getDomainPattern = (record) => getField(record, 'domainPattern', 'DomainPattern') || '';
const getRuleType = (record) => getField(record, 'ruleType', 'RuleType') || 'exclude';
const getCreatedBy = (record) => getField(record, 'createdBy', 'CreatedBy') || '';
const getCreatedDate = (record) => getField(record, 'createdDate', 'CreatedDate') || '';
const getId = (record) => getField(record, 'id', 'Id');

const RULE_TYPE_OPTIONS = [
    { value: 'exclude', label: 'Exclude' },
    { value: 'include', label: 'Include' }
];

const TYPE_FILTER_OPTIONS = [
    { value: 'all', label: 'All' },
    ...RULE_TYPE_OPTIONS
];

const findOptionByValue = (options, value) =>
    options.find((option) => String(option.value) === String(value)) || null;

const getApiMessage = (response, fallback) => {
    const data = response?.data;

    if (!data) return fallback;
    if (typeof data === 'string') return data;

    return data.message || data.Message || data.error || data.Error || fallback;
};

const normalizeDomainPattern = (value) => {
    return value.trim().toLowerCase();
};

const normalizeRuleType = (value) => (
    String(value || '').trim().toLowerCase() === 'include' ? 'include' : 'exclude'
);

const formatRuleType = (value) => {
    const ruleType = normalizeRuleType(value);
    return ruleType === 'include' ? 'Include' : 'Exclude';
};

const isValidDomainPattern = (value) => {
    const isWildcardPattern = value.startsWith('*');
    const domain = value.startsWith('*.') ? value.slice(2) : isWildcardPattern ? value.slice(1) : value;

    return (
        Boolean(domain) &&
        !domain.includes(' ') &&
        !domain.includes('/') &&
        !domain.startsWith('.') &&
        !domain.endsWith('.') &&
        (DOMAIN_PATTERN_REGEX.test(domain) || (isWildcardPattern && TLD_PATTERN_REGEX.test(domain)))
    );
};

const formatCreatedDate = (value) => {
    if (!value) return 'N/A';

    try {
        return trimInvalidDate(value) || 'N/A';
    } catch (error) {
        return 'N/A';
    }
};

const getDateTime = (value) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const SORT_COLUMNS = {
    domain: {
        label: 'Domain',
        defaultDirection: 'asc',
        getValue: (row) => row.domain
    },
    ruleType: {
        label: 'Type',
        defaultDirection: 'asc',
        getValue: (row) => row.ruleTypeDisplay
    },
    createdBy: {
        label: 'Created By',
        defaultDirection: 'asc',
        getValue: (row) => row.createdByDisplay
    },
    createdDate: {
        label: 'Created Date',
        defaultDirection: 'desc',
        getValue: (row) => row.createdDateTime
    }
};

const getNormalizedRow = (record) => {
    const domain = getDomainPattern(record);
    const ruleType = normalizeRuleType(getRuleType(record));
    const createdDate = getCreatedDate(record);

    return {
        id: getId(record),
        domain,
        domainKey: normalizeDomainPattern(domain),
        ruleType,
        ruleTypeDisplay: formatRuleType(ruleType),
        createdByDisplay: getCreatedBy(record) || 'N/A',
        createdDateDisplay: formatCreatedDate(createdDate),
        createdDateTime: getDateTime(createdDate)
    };
};

const getDomainKeySet = (records) => (
    new Set(records.map((record) => {
        const row = getNormalizedRow(record);
        return `${row.domainKey}|${row.ruleType}`;
    }))
);

const ExcludedDomains = () => {
    const showSnackbar = useShowSnackbar();
    const showSnackbarRef = useRef(showSnackbar);

    const [domains, setDomains] = useState([]);
    const [domainPattern, setDomainPattern] = useState('');
    const [ruleType, setRuleType] = useState('exclude');
    const [typeFilter, setTypeFilter] = useState('all');
    const [draftType, setDraftType] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [domainError, setDomainError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [sortConfig, setSortConfig] = useState({
        key: 'domain',
        direction: 'asc'
    });

    useEffect(() => {
        showSnackbarRef.current = showSnackbar;
    }, [showSnackbar]);

    const fetchDomainList = useCallback(async () => {
        const response = await rget(`${DEV_URL}/api/Company/DomainIgnoreList`);

        if (response?.status !== 200) {
            throw new Error(getApiMessage(response, 'Failed to load excluded domains'));
        }

        const payload = response?.data?.data || response?.data?.Data || response?.data || [];

        return Array.isArray(payload) ? payload : [];
    }, []);

    const fetchDomains = useCallback(async () => {
        setIsLoading(true);

        try {
            const list = await fetchDomainList();
            setDomains(list);
        } catch (error) {
            showSnackbarRef.current(error?.message || 'Failed to load excluded domains', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [fetchDomainList]);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    const domainRows = useMemo(() => domains.map((record) => getNormalizedRow(record)), [domains]);

    const domainKeys = useMemo(
        () => new Set(domainRows.map((row) => `${row.domainKey}|${row.ruleType}`)),
        [domainRows]
    );

    const sortedRows = useMemo(() => {
        const searchValue = appliedSearch.trim().toLowerCase();
        const column = SORT_COLUMNS[sortConfig.key];
        const filteredList = domainRows.filter((row) => {
            const matchesSearch = !searchValue || row.domain.toLowerCase().includes(searchValue);
            const matchesType = typeFilter === 'all' || row.ruleType === typeFilter;

            return matchesSearch && matchesType;
        });

        return [...filteredList].sort((left, right) => {
            const leftValue = column.getValue(left);
            const rightValue = column.getValue(right);
            let comparison = 0;

            if (typeof leftValue === 'number' && typeof rightValue === 'number') {
                comparison = leftValue - rightValue;
            } else {
                comparison = String(leftValue).localeCompare(String(rightValue), undefined, {
                    sensitivity: 'base',
                    numeric: true
                });
            }

            if (comparison === 0) {
                comparison = left.domain.localeCompare(right.domain, undefined, {
                    sensitivity: 'base',
                    numeric: true
                });
            }

            if (comparison === 0) {
                comparison = left.ruleTypeDisplay.localeCompare(right.ruleTypeDisplay, undefined, {
                    sensitivity: 'base',
                    numeric: true
                });
            }

            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [appliedSearch, domainRows, sortConfig, typeFilter]);

    const handleSort = (key) => {
        setSortConfig((current) => {
            if (current.key === key) {
                return {
                    key,
                    direction: current.direction === 'asc' ? 'desc' : 'asc'
                };
            }

            return {
                key,
                direction: SORT_COLUMNS[key].defaultDirection
            };
        });
    };

    const renderSortHeader = (key) => {
        const column = SORT_COLUMNS[key];
        const isActive = sortConfig.key === key;

        return (
            <button
                type="button"
                className="btn btn-link p-0 text-decoration-none text-dark fw-bold d-inline-flex align-items-center"
                onClick={() => handleSort(key)}
            >
                <span className="me-1">{column.label}</span>
                <SortingIcon isSorted={isActive ? sortConfig.direction : false} />
            </button>
        );
    };

    const getAriaSort = (key) => {
        if (sortConfig.key !== key) return 'none';
        return sortConfig.direction === 'asc' ? 'ascending' : 'descending';
    };


    const applySearchFilters = () => {
        setAppliedSearch(searchText.trim() || appliedSearch);
        setTypeFilter(draftType);
        setSearchText('');
    };
    const handleResetSearch = () => {
        setDraftType('all');
        setSearchText('');
        setAppliedSearch('');
        setTypeFilter('all');
    };


    const validateDomain = (currentDomainKeys = domainKeys) => {
        const normalizedValue = normalizeDomainPattern(domainPattern);

        if (!normalizedValue) {
            return { isValid: false, value: normalizedValue, message: 'Domain name is required' };
        }

        if (!isValidDomainPattern(normalizedValue)) {
            return {
                isValid: false,
                value: normalizedValue,
                message: 'Enter a valid domain name. Use example.com, *.example.com, or *.law.'
            };
        }

        if (currentDomainKeys.has(`${normalizedValue}|${normalizeRuleType(ruleType)}`)) {
            return {
                isValid: false,
                value: normalizedValue,
                message: 'Domain already exists with this type'
            };
        }

        return { isValid: true, value: normalizedValue, message: '' };
    };

    const handleAddDomain = async (event) => {
        event.preventDefault();

        const validation = validateDomain();
        setDomainPattern(validation.value);

        if (!validation.isValid) {
            setDomainError(validation.message);
            return;
        }

        setDomainError('');
        setIsSaving(true);

        try {
            const latestDomains = await fetchDomainList();
            setDomains(latestDomains);

            const latestValidation = validateDomain(getDomainKeySet(latestDomains));

            if (!latestValidation.isValid) {
                setDomainPattern(latestValidation.value);
                setDomainError(latestValidation.message);
                return;
            }

            const response = await rpost(`${DEV_URL}/api/Company/DomainIgnoreList`, {
                DomainPattern: validation.value,
                RuleType: normalizeRuleType(ruleType)
            });

            if (response?.status === 200) {
                showSnackbar(getApiMessage(response, 'Domain added successfully'), 'success');
                setDomainPattern('');
                setRuleType('exclude');
                await fetchDomains();
            } else {
                showSnackbar(getApiMessage(response, 'Failed to add domain'), 'error');
            }
        } catch (error) {
            showSnackbar(error?.message || 'Failed to add domain', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveDomain = async (row) => {
        if (!row.id) {
            showSnackbar('Unable to remove domain', 'error');
            return;
        }

        setRemovingId(row.id);

        try {
            const response = await rdelete(`${DEV_URL}/api/Company/DomainIgnoreList/${row.id}`);

            if (response?.status === 200) {
                setDomains((current) => current.filter((item) => getId(item) !== row.id));
                showSnackbar(getApiMessage(response, 'Domain removed from ignore list'), 'success');
            } else {
                showSnackbar(getApiMessage(response, 'Failed to remove domain'), 'error');
            }
        } catch (error) {
            showSnackbar('Failed to remove domain', 'error');
        } finally {
            setRemovingId(null);
        }
    };

    const noDataMessage = appliedSearch.trim() || typeFilter !== 'all' ? 'No matching domains found' : 'No excluded domains added';

    return (
        <>
            <h4>Excluded Domains</h4>

            <Row>
                <Col md={12}>
                    <Card>
                        <Card.Body>
                            <Form onSubmit={handleAddDomain}>
                                <Row className="align-items-start g-2">
                                    <Col lg={5}>
                                        <FormInput
                                            type="text"
                                            name="domainPattern"
                                            label="Domain Name"
                                            labelClassName="fw-bold"
                                            value={domainPattern}
                                            placeholder="Enter domain.com for exact match or *.domain.com for pattern ignore"
                                            disabled={isSaving}
                                            errors={domainError ? { domainPattern: { message: domainError } } : undefined}
                                            onChange={(event) => {
                                                setDomainPattern(event.target.value);
                                                setDomainError('');
                                            }}
                                        />
                                    </Col>

                                    <Col lg={2} md={4}>
                                        <Form.Label className="fw-bold">Type</Form.Label>
                                        <Select
                                            value={findOptionByValue(RULE_TYPE_OPTIONS, ruleType)}
                                            options={RULE_TYPE_OPTIONS}
                                            isDisabled={isSaving}
                                            onChange={(option) => setRuleType(normalizeRuleType(option?.value))}
                                            isSearchable={false}
                                            isClearable={false}
                                        />
                                    </Col>

                                    <Col lg="auto">
                                        <Form.Label className="d-none d-lg-block invisible">Action</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            <LoadingButton type="button" variant="primary" disabled={isLoading} onClick={applySearchFilters}>Apply</LoadingButton>
                                            <LoadingButton type="button" variant="outline-secondary" disabled={isLoading || (!searchText && !appliedSearch && typeFilter === 'all' && draftType === 'all')} onClick={handleResetSearch}>Reset</LoadingButton>
                                        <LoadingButton
                                            type="submit"
                                            disabled={isSaving || isLoading}
                                            isLoading={isSaving}
                                            iconOrientation="start"
                                        >
                                            Add Domain
                                        </LoadingButton>
                                        </div>
                                    </Col>

                                    <div className="w-100" />

                                    <Col lg={5}>
                                        <TableSearch value={searchText} onChange={setSearchText} onSearch={applySearchFilters}
                                            showAction={false} showApplied={false}
                                            label="Search Domain" placeholder="Search by domain name" disabled={isLoading} />
                                    </Col>

                                    <Col md={4} lg={2} className="align-self-end">
                                        <Select
                                            value={findOptionByValue(TYPE_FILTER_OPTIONS, draftType)}
                                            options={TYPE_FILTER_OPTIONS}
                                            isDisabled={isLoading}
                                            onChange={(option) => setDraftType(option?.value || 'all')}
                                            isSearchable={false}
                                            isClearable={false}
                                        />
                                    </Col>

                                </Row>
                            </Form>
                            <FilterPills disabled={isLoading} filters={[
                                { label: 'Search', value: appliedSearch, onRemove: () => setAppliedSearch('') },
                                { label: 'Type', value: typeFilter === 'all' ? '' : findOptionByValue(TYPE_FILTER_OPTIONS, typeFilter)?.label,
                                    onRemove: () => { setTypeFilter('all'); setDraftType('all'); } },
                            ]} />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={12}>
                    <Card>
                        <Card.Body>
                            <div className="table-responsive">
                                <Table className="table-centered mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th aria-sort={getAriaSort('domain')}>
                                                {renderSortHeader('domain')}
                                            </th>
                                            <th aria-sort={getAriaSort('ruleType')}>
                                                {renderSortHeader('ruleType')}
                                            </th>
                                            <th aria-sort={getAriaSort('createdBy')}>
                                                {renderSortHeader('createdBy')}
                                            </th>
                                            <th aria-sort={getAriaSort('createdDate')}>
                                                {renderSortHeader('createdDate')}
                                            </th>
                                            <th className="text-end">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan={5} className="text-center py-4">
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Loading...
                                                </td>
                                            </tr>
                                        ) : sortedRows.length > 0 ? (
                                            sortedRows.map((row) => (
                                                <tr key={row.id || `${row.domain}-${row.ruleType}`}>
                                                    <td className="fw-bold">{row.domain}</td>
                                                    <td>{row.ruleTypeDisplay}</td>
                                                    <td>{row.createdByDisplay}</td>
                                                    <td>{row.createdDateDisplay}</td>
                                                    <td className="text-end">
                                                        <LoadingButton
                                                            variant="outline-danger"
                                                            size="sm"
                                                            disabled={removingId === row.id}
                                                            isLoading={removingId === row.id}
                                                            iconOrientation="start"
                                                            onClick={() => handleRemoveDomain(row)}
                                                        >
                                                            Remove
                                                        </LoadingButton>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} className="text-center py-4">
                                                    {noDataMessage}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
};

export default ExcludedDomains;
